<script>
    import Icon from '$component/Icon.svelte';
    import Title from "$component/Title.svelte";
    import { error, items, loading, message } from "$lib/basketStore.svelte.js";
    import { t } from "$utils/lang.js";
    import BasketItem from "$component/BasketItem.svelte";
    import BasketSum from "$component/BasketSum.svelte";
    import AddressForm from "$component/AddressForm.svelte";
    import AddressCard from "$component/AddressCard.svelte";
    import ConfirmModal from "$component/ConfirmModal.svelte";
    import AddressValidationModal from "$component/AddressValidationModal.svelte";
    import VatValidationModal from "$component/VatValidationModal.svelte";
    import ValidationError from "$component/ValidationError.svelte";
    import {
        BoxIcon,
        Checkbox,
        EmailInput,
        HandShakeIcon,
        HouseIcon,
        PasswordInput,
        Radiobox,
        TextInput,
        buttonStyles,
    } from "m7-library";

    import { store as userStore, login, logout, fetchUser, saveUser, validateVatId, selectAddress, deleteAddress, Address, countryName, fetchMarketCountry } from "$lib/userStore.svelte";
    import {addressRejection} from '$lib/addressRejection.svelte.js';
    import { addressFieldErrors, amsNeedsReview, addressNeedsReview } from "$lib/addressValidation.js";
    import { paymentStore, patchExpressShipping } from "$lib/paymentStore.svelte.js";
    import { devLog as _devLog } from "$utils/devLog.js";
    import Packstation from "$component/Packstation.svelte";
    import {angebote, ladeAngebote} from '$lib/angebote.svelte.js';
    import { untrack } from "svelte";
    import { SvelteSet } from "svelte/reactivity";
    import { scrollToFirstError } from "$utils/scrollToError.js";

    const devLog = (tag, ...args) => _devLog(`User:${tag}`, ...args);

    let {
        status = 'active',
        onContinue = () => {},
        onActivate = () => {},
        subRoute = $bindable(''),
        isExpressMode = false,
        allowAddressChange = true,
        expressLoginRequired = false,
        onExpressLoginComplete = () => {},
        nonShippableCountry = false,
        nonShippableCountryName = '',
        onAddressChanged = () => {},
    } = $props();

    $effect(() => {
        if (expressLoginRequired) showLogin = true;
    });

    // Clear the non-shippable-country banner once the user acts to change the
    // address: enters an edit/select surface (addressMode change) OR the chosen
    // delivery COUNTRY changes (covers the guest form's country select, which
    // keeps addressMode='default'). We arm a baseline on the first run while
    // the banner is up to avoid the logged-in auto-select effect clearing it
    // prematurely; a non-empty country differing from the baseline = real
    // change. The payment step is the authoritative shippability gate, so a
    // generous clear here is safe — a still-non-shippable address is caught
    // there (0 shipping methods → continue blocked + message).
    let _nonShipArmed = $state(false);
    let _nonShipBaselineCid = $state(null);
    $effect(() => {
        const mode = addressMode;
        const cid = useBillingAsShipping
            ? user.billingAddress?.countryId
            : (activeDelivery?.countryId ?? guestDeliveryAddress?.countryId ?? user.shippingAddress?.countryId);
        if (!nonShippableCountry) { _nonShipArmed = false; return; }
        if (!_nonShipArmed) { _nonShipArmed = true; _nonShipBaselineCid = cid; return; }
        if (mode !== 'default' || (cid && cid !== _nonShipBaselineCid)) onAddressChanged();
    });

    function maybePatchExpressShipping() {
        if (!isExpressMode) return;
        // billing-as-shipping → use billing addr (Express payer addr = shipping addr)
        // separate delivery   → prefer activeDelivery (most fresh), fall back to shippingAddress
        const addr = useBillingAsShipping
            ? userStore.user.billingAddress
            : (activeDelivery ?? userStore.user.shippingAddress);

        devLog('maybePatchExpressShipping', {
            useBillingAsShipping,
            activeDeliveryId,
            resolved: { street: addr.street, streetNo: addr.streetNo, zip: addr.zip, city: addr.city, countryCode: addr.countryCode },
            billing: { countryCode: userStore.user.billingAddress.countryCode },
            shippingStore: { countryCode: userStore.user.shippingAddress.countryCode },
        });

        patchExpressShipping({
            street: addr.street,
            streetNo: addr.streetNo,
            zip: addr.zip,
            city: addr.city,
            countryCode: addr.countryCode,
        });
    }

    // URL ↔ addressMode mapping (so reload/share preserves the inner state for debugging)
    // '' ↔ default, 'change' ↔ select, 'new' ↔ new (useNewAddress=true),
    // 'edit' ↔ new (useNewAddress=false), 'edit-billing' ↔ editBilling
    function subRouteToMode(sr) {
        switch (sr) {
            case 'change': return 'select';
            case 'new': return 'new';
            case 'edit': return 'new';
            case 'edit-billing': return 'editBilling';
            default: return 'default';
        }
    }
    function modeToSubRoute(m, isNewAddr) {
        switch (m) {
            case 'select': return 'change';
            case 'new': return isNewAddr ? 'new' : 'edit';
            case 'editBilling': return 'edit-billing';
            default: return '';
        }
    }

    const user = $derived(userStore.user);

    let showLogin = $state(false);
    let loginError = $state('');
    let loginLoading = $state(false);
    // `addressType` is the active radio in 'new' mode + the form choice in guest flow.
    // For logged-in default-mode validation, prefer the derived `activeAddressType` (below)
    // which reflects the actually-selected delivery address.
    let addressType = $state("address");
    let errors = $state({});
    let saving = $state(false);
    const deliveryAddresses = $derived(userStore.deliveryAddresses || []);
    let initialActiveDeliveryId = $state(null);

    // ─── Single Source of Truth: activeDeliverySelection ─────────────────────
    // Discriminated union, replaces the old `selectedAddressId + BILLING_ID sentinel`.
    //   { kind: 'billing' }                ← use billing address as shipping
    //   { kind: 'address', id: '<oxid>' }  ← use a specific oxaddress row
    let activeDeliverySelection = $state({ kind: 'billing' });

    // Packstation only available when DHL shipping method is in the current set
    // and this shop offers it. reference: [oxid-proxy services/shop/angebote 1]
    ladeAngebote();
    const hasDhlShipping = $derived(
        angebote.packstation
        && (paymentStore.shippingMethods ?? []).some(s => s.shippingSet?.deliveryType === 'dhl')
    );
    // Reset addressType to 'address' if DHL disappears (e.g. bike added to cart)
    $effect(() => {
        if (!hasDhlShipping && addressType === 'packstation') addressType = 'address';
    });

    // Derived selection projections — never write to these.
    const useBillingAsShipping = $derived(activeDeliverySelection.kind === 'billing');
    const activeDeliveryId = $derived(
        activeDeliverySelection.kind === 'address' ? activeDeliverySelection.id : null
    );
    const activeDelivery = $derived(
        activeDeliveryId
            ? (deliveryAddresses.find(a => a.id === activeDeliveryId) ?? null)
            : null
    );
    const activeAddressType = $derived(
        activeDelivery?.street === 'Packstation' ? 'packstation' : 'address'
    );

    // Effective shipping address for AMS validation. selectAddress() switches
    // the backend deladrid + refreshes basket but does NOT sync the store
    // shippingAddress (only saveUser / fetchUser do). So after the user picks
    // a different saved delivery row, `user.shippingAddress` still holds the
    // PREVIOUSLY saved address while `activeDelivery` is the freshly selected
    // one. Validating/showing the stale shippingAddress made the modal pop
    // with row A's suggestion while row B was active — and the edit button
    // (which loads activeDelivery) then opened B → A/B mismatch. Prefer the
    // active row, fall back to the store address for the new-address form.
    const activeShippingAddress = $derived(activeDelivery ?? user.shippingAddress);

    // Backwards-compat shim for guest flow + a few residual reads:
    // hideShipping is now read-only derived from selection.
    const hideShipping = $derived(useBillingAsShipping);

    let useNewAddress = $state(true);
    let stepEl = $state(); // active-step root — anchor for shadow-DOM error scroll
    let addressMode = $state(subRouteToMode(subRoute)); // 'default' | 'select' | 'new' | 'editBilling'
    let previousAddressMode = $state('default'); // mode to return to when canceling editBilling

    // Two-way sync: addressMode ↔ subRoute (URL hash via parent).
    // INCOMING (external subRoute change → local mode) runs as $effect.pre so it
    // fires BEFORE the OUTGOING effect on the same tick. Otherwise the outgoing
    // effect would re-write subRoute back to its old value before this one
    // could update addressMode.
    // Only `subRoute` (and `activeDeliverySelection` for context validation) should be
    // tracked here — reading `addressMode`/`useNewAddress` would make them dependencies
    // too, and the effect would then re-fire on local mode changes (e.g. a button click)
    // and overwrite them back to the subroute-derived value before the OUTGOING effect
    // can sync the URL.
    $effect.pre(() => {
        const m = subRouteToMode(subRoute);
        const kindIsAddress = activeDeliverySelection.kind === 'address';
        untrack(() => {
            // Subroute kontext-validation: 'edit' requires an actual selected delivery row.
            if (subRoute === 'edit' && !kindIsAddress) {
                addressMode = 'select';
                useNewAddress = false;
                return;
            }
            if (m !== addressMode) addressMode = m;
            // 'new' subroute means brand-new; 'edit' means editing the selected delivery.
            if (subRoute === 'new' && !useNewAddress) useNewAddress = true;
            if (subRoute === 'edit' && useNewAddress) useNewAddress = false;
        });
    });
    // OUTGOING (local mode change → URL). Track addressMode/useNewAddress only;
    // reading subRoute would create a feedback loop with the INCOMING effect.
    $effect(() => {
        const sr = modeToSubRoute(addressMode, useNewAddress);
        untrack(() => {
            if (sr !== subRoute) subRoute = sr;
        });
    });
    // When address changes are locked (e.g. Amazon Pay Express), prevent navigation
    // into edit modes — redirect back to default view.
    $effect(() => {
        if (!allowAddressChange && (addressMode === 'select' || addressMode === 'editBilling' || addressMode === 'new')) {
            addressMode = 'default';
        }
    });

    // Decoupled draft address for the 'new' mode (logged-in users editing/adding a
    // delivery address). Binding the form to user.billingAddress would mutate the
    // user's home address — see saveCheckoutUser, where billing always writes to oxuser.
    let newAddress = $state(new Address());

    // Guest checkout: billing address checkbox + separate delivery address state
    let billingSameAsDelivery = $state(true);
    // Standardland = Land des Markts (mount7.pl: Polen), Name aus dem Proxy. reference: [lib/userStore.svelte 1]
    let marktLand = $state(null);
    fetchMarketCountry().then((land) => { marktLand = land; });
    // Packstation ist immer Deutschland (DHL); nur der Name kommt aus der Länderliste.
    const DE_COUNTRY_ID = 'a7c40f631fc920687.20179984';
    let deName = $state('');
    countryName(DE_COUNTRY_ID).then((name) => { deName = name; });
    let guestDeliveryAddress = $state(new Address());
    // Gast-Lieferadresse ohne Land bekommt das Marktland, sobald es geladen ist.
    $effect(() => {
        if (!marktLand) return;
        untrack(() => {
            if (!guestDeliveryAddress.countryId) {
                guestDeliveryAddress.countryId = marktLand.id;
                guestDeliveryAddress.countryCode = marktLand.code;
                guestDeliveryAddress.country = marktLand.title;
            }
        });
    });
    let billingErrors = $state({});
    // Plain (non-reactive) flag — intentionally not $state so it doesn't create
    // a dependency cycle when we set it inside the restore $effect.
    let guestStateInitialized = false;

    // Pre-fill country from billing address as soon as user data is available.
    // Uses untrack so mutations to newAddress don't re-trigger this effect.
    $effect(() => {
        const billingCountryId = user?.billingAddress?.countryId;
        if (billingCountryId && !newAddress.countryId) {
            untrack(() => {
                newAddress.countryId = billingCountryId;
                newAddress.country = user.billingAddress.country ?? '';
                newAddress.countryCode = user.billingAddress.countryCode ?? '';
            });
        }
    });

    // Guest checkout: pre-fill the market country when no billing country set (fires after fetchUser too).
    $effect(() => {
        if (isLoggedIn || !marktLand) return;
        const countryId = user?.billingAddress?.countryId;
        if (!countryId) {
            untrack(() => {
                user.billingAddress.countryId = marktLand.id;
                user.billingAddress.country = marktLand.title;
                user.billingAddress.countryCode = marktLand.code;
            });
        }
    });

    // Delete-confirmation modal state
    let deleteModalOpen = $state(false);
    let pendingDeleteId = $state(null);
    let countries = $state([]);

    // Packstation
    let packstationPostnummer = $state('');
    let packstationNummer = $state('');
    let packstationPlz = $state('');
    let packstationOrt = $state('');
    let selectedPackstation = $state(null);
    let packstationOpen = $state(false);

    function handlePackstationSelect({ station }) {
        const place = station.place || {};
        const addr = place.address || {};
        selectedPackstation = {
            name: station.location?.keyword || `Packstation ${station.location?.keywordId || ''}`,
            address: addr.streetAddress || '',
            zip: addr.postalCode || '',
            city: addr.addressLocality || '',
            number: station.location?.keywordId || '',
        };
        packstationNummer = selectedPackstation.number;
        packstationPlz = selectedPackstation.zip;
        packstationOrt = selectedPackstation.city;
    }

    const hasUser = $derived(!!user.id);
    const isLoggedIn = $derived(user.isLoggedIn);

    // Is the active delivery address the same as the user's billing (home) address?
    const isDeliveryEqualBilling = $derived(() => {
        if (useBillingAsShipping) return true;
        if (!activeDelivery || !user.billingAddress) return true;
        const d = activeDelivery;
        const b = user.billingAddress;
        return d.street === b.street && d.streetNo === b.streetNo && d.zip === b.zip && d.city === b.city;
    });

    // Snapshot of the billing address shaped like an Address (for AddressCard)
    const billingAsAddress = $derived({
        id: null,
        name: user.billingAddress.name,
        street: user.billingAddress.street,
        streetNo: user.billingAddress.streetNo,
        zip: user.billingAddress.zip,
        city: user.billingAddress.city,
        country: user.billingAddress.country,
        countryCode: user.billingAddress.countryCode,
        company: user.billingAddress.company,
        ustid: user.billingAddress.ustid,
        addInfo: user.billingAddress.addInfo,
    });

    // Log address selection changes
    $effect(() => {
        const sel = activeDeliverySelection;
        devLog('addressSelection', sel.kind === 'billing'
            ? {kind: 'billing'}
            : {kind: 'address', id: sel.id, city: activeDelivery?.city, deliveryType: activeDelivery?.street === 'Packstation' ? 'packstation' : 'address'}
        );
    });

    // Log address mode changes (user navigates between views)
    $effect(() => {
        devLog('addressMode', {mode: addressMode, isLoggedIn});
    });

    // Log guest billing checkbox changes
    $effect(() => {
        if (!isLoggedIn) devLog('billingSameAsDelivery', {value: billingSameAsDelivery});
    });

    // Restore guest delivery state from server after fetchUser (page reload / back-navigation).
    // The server returns shippingAddress when session.deladrid is set (separate delivery was saved).
    // Runs once; guestStateInitialized (plain var) prevents re-running on subsequent data changes.
    $effect(() => {
        if (isLoggedIn || guestStateInitialized) return;
        const street = user.shippingAddress.street;
        const city = user.shippingAddress.city;
        if (!street && !city) return; // no separate delivery address in session
        devLog('guestStateRestore', {street, city, name: user.shippingAddress.name, zip: user.shippingAddress.zip});
        untrack(() => {
            guestStateInitialized = true;
            billingSameAsDelivery = false;
            guestDeliveryAddress = new Address(user.shippingAddress);
        });
    });

    async function handleLogin() {
        loginError = '';
        loginLoading = true;
        devLog('btn:login', {email: user.email});
        // Password managers may set input values without firing input events.
        // Read DOM values directly so autofilled credentials are always picked up.
        const emailEl = document.querySelector('input[name="email"]');
        const pwEl = document.querySelector('input[name="password"]');
        if (emailEl?.value) user.email = emailEl.value;
        if (pwEl?.value) user.password = pwEl.value;
        try {
            await login();
            if (userStore.loginError || !userStore.user.isLoggedIn) {
                devLog('login:error', {error: userStore.loginError});
                loginError = userStore.loginError || t('Anmeldung fehlgeschlagen. Bitte überprüfe deine Zugangsdaten.');
            } else {
                devLog('login:success', {email: user.email});
                showLogin = false;
                onExpressLoginComplete();
            }
        } catch (e) {
            devLog('login:error', {error: e.message});
            loginError = t('Anmeldung fehlgeschlagen. Bitte überprüfe deine Zugangsdaten.');
        } finally {
            loginLoading = false;
        }
    }

    function validate() {
        const e = {};
        billingErrors = {};
        if (!user.email || !user.email.includes('@')) {
            e.email = t('Bitte gib eine gültige E-Mail-Adresse ein.');
        }

        // For logged-in users: derive the effective shipping type from the actually
        // selected delivery row, NOT the radio (which only exists in 'new' mode / guest).
        const effectiveType = isLoggedIn ? activeAddressType : addressType;

        if (effectiveType === 'packstation') {
            // For logged-in: packstation data comes from activeDelivery (already saved oxaddress).
            // For guest: packstation data is in the local packstation* state.
            if (!isLoggedIn) {
                if (!selectedPackstation) e.packstationNummer = t('Bitte wähle eine Packstation aus.');
                if (!packstationPostnummer) e.postnummer = t('Bitte gib deine DHL Postnummer ein.');
                // Guests must provide a billing address even when shipping to a Packstation.
                const addr = user.billingAddress;
                if (!addr.name || addr.name.trim().split(' ').length < 2) {
                    e.name = t('Bitte gib Vor- und Nachname ein.');
                }
                if (!addr.street) e.street = t('Bitte gib eine Straße ein.');
                if (!addr.streetNo) e.streetNo = t('Bitte gib eine Hausnummer ein.');
                if (!addr.zip) e.zip = t('Bitte gib eine Postleitzahl ein.');
                if (!addr.city) e.city = t('Bitte gib einen Ort ein.');
                if (!addr.countryId) e.countryId = t('Bitte wähle ein Land.');
            }
        } else if (!isLoggedIn) {
            // Always validate guestDeliveryAddress as the delivery address
            const deliveryAddr = guestDeliveryAddress;
            if (!deliveryAddr.name || deliveryAddr.name.trim().split(' ').length < 2) {
                e.name = t('Bitte gib Vor- und Nachname ein.');
            }
            if (!deliveryAddr.street) e.street = t('Bitte gib eine Straße ein.');
            if (!deliveryAddr.streetNo) e.streetNo = t('Bitte gib eine Hausnummer ein.');
            if (!deliveryAddr.zip) e.zip = t('Bitte gib eine Postleitzahl ein.');
            if (!deliveryAddr.city) e.city = t('Bitte gib einen Ort ein.');
            if (!deliveryAddr.countryId) e.countryId = t('Bitte wähle ein Land.');

            if (!billingSameAsDelivery) {
                const be = {};
                const bill = user.billingAddress;
                if (!bill.name || bill.name.trim().split(' ').length < 2) be.name = t('Bitte gib Vor- und Nachname ein.');
                if (!bill.street) be.street = t('Bitte gib eine Straße ein.');
                if (!bill.streetNo) be.streetNo = t('Bitte gib eine Hausnummer ein.');
                if (!bill.zip) be.zip = t('Bitte gib eine Postleitzahl ein.');
                if (!bill.city) be.city = t('Bitte gib einen Ort ein.');
                if (!bill.countryId) be.countryId = t('Bitte wähle ein Land.');
                billingErrors = be;
            }
        }

        // Logged-in users with a real delivery row: trust the server-validated address.
        // No client-side shipping-fields check — the row was saved through saveCheckoutUser
        // which already validated.

        errors = e;
        return Object.keys(e).length === 0 && Object.keys(billingErrors).length === 0;
    }

    // ─── Address-validation modal state (AMS / Phase 3) ─────────────────────
    //
    // After every saveUser() the backend may stamp the saved billing / shipping
    // row with one of the non-OK MOJOAMSSTATUS values
    // ('address_needs_correction', 'address_multiple_variants',
    // 'address_not_found'). We open the AddressValidationModal once per
    // address-shape and remember the user's "accepted" decision in a Set keyed
    // by a fingerprint of the address fields. The next saveUser with the same
    // shape skips the modal so the user isn't re-prompted on every continue
    // click. The accepted-set is intentionally session-local: a real edit
    // changes the fingerprint and triggers a fresh prompt.
    let validationModal = $state({
        open: false,
        target: 'billing', // 'billing' | 'shipping'
        original: null,
        predictions: null,
        status: 'address_needs_correction',
    });
    // Reactive Set: $derived.by(hasPendingAmsReview) and pickValidationTarget
    // read this on every continue. A plain Set mutated via .add() does NOT
    // invalidate Svelte 5 deriveds, which left the continue-gate stale-true
    // after "Original übernehmen" (AMS deadlock). SvelteSet is tracked.
    const acceptedAmsFingerprints = new SvelteSet();

    function amsFingerprint(addr) {
        if (!addr) return '';
        const norm = (s) => (s ?? '').toString().trim().toLowerCase();
        return [norm(addr.street), norm(addr.streetNo), norm(addr.zip), norm(addr.city), norm(addr.countryId)].join('|');
    }

    // ─── VAT-ID validation modal state ──────────────────────────────────────
    //
    // After a billing save with a USt-IdNr. the `validateVatId` endpoint cross-
    // checks the address against VIES and returns a `checks` array. When at
    // least one check failed (and taxInfo !== -1) we surface the legacy ✅/❌
    // checklist in a modal before letting the user continue. "Trotzdem
    // fortfahren" records a session-local accept keyed by a fingerprint of the
    // billing fields involved, so a real edit re-prompts while a no-op re-click
    // skips. Same mental model as the AMS accepted-set above.
    let vatValidationModal = $state({ open: false });
    const acceptedVatFingerprints = new SvelteSet();

    function vatFingerprint() {
        const b = user?.billingAddress;
        if (!b) return '';
        const norm = (s) => (s ?? '').toString().trim().toLowerCase();
        return [norm(b.ustid), norm(b.company), norm(b.street), norm(b.streetNo), norm(b.zip), norm(b.city), norm(b.countryId)].join('|');
    }

    // True when the last VAT validation reported a real mismatch the user
    // hasn't accepted yet: taxInfo !== -1, a checks array exists, and at least
    // one check failed. All ✅ → no modal, no noise.
    function vatNeedsReview() {
        const r = userStore.vatValidationResult;
        if (!r || r.taxInfo === -1) return false;
        if (!Array.isArray(r.checks) || !r.checks.some(c => c && c.ok === false)) return false;
        return !acceptedVatFingerprints.has(vatFingerprint());
    }

    // Modal resolve handler — wired into VatValidationModal.
    function handleVatValidationResolve(action) {
        vatValidationModal.open = false;
        if (action === 'accept') {
            // Remember the decision and continue the normal flow.
            acceptedVatFingerprints.add(vatFingerprint());
            subRoute = '';
            onContinue();
        } else if (action === 'edit') {
            // Drop the user into the billing edit surface so they can fix the
            // mismatching fields. Guests already have the billing form inline.
            if (isLoggedIn) {
                previousAddressMode = addressMode === 'default' ? 'default' : addressMode;
                addressMode = 'editBilling';
            }
        }
        // 'dismiss' → no-op; the continue gate re-opens on the next attempt.
    }

    // Pick the first target (billing wins, then shipping) whose AMS status
    // requires review AND hasn't been "use-original"-accepted by the user
    // already in this session. Returns null when there's nothing to ask about.
    function pickValidationTarget() {
        const billing = user.billingAddress;
        if (addressNeedsReview(billing) && !acceptedAmsFingerprints.has(amsFingerprint(billing))) {
            return {
                target: 'billing',
                original: billing,
                predictions: billing.amsPredictions,
                status: billing.amsStatus,
            };
        }
        // Shipping address only matters when it's a separate row (not when
        // billing-as-shipping is active — in that case it'd be redundant
        // with the billing check above).
        if (!useBillingAsShipping) {
            const shipping = activeShippingAddress;
            if (addressNeedsReview(shipping) && !acceptedAmsFingerprints.has(amsFingerprint(shipping))) {
                return {
                    target: 'shipping',
                    original: shipping,
                    predictions: shipping.amsPredictions,
                    status: shipping.amsStatus,
                };
            }
        }
        return null;
    }

    // True when continuing past User would skip the AMS review — i.e. there
    // IS an open AMS issue that the user hasn't decided on yet. Used to
    // disable the continue button as a defensive UI hint; handleSubmit also
    // opens the modal inline before calling onContinue().
    const hasPendingAmsReview = $derived.by(() => {
        const billing = user?.billingAddress;
        if (addressNeedsReview(billing) && !acceptedAmsFingerprints.has(amsFingerprint(billing))) {
            return true;
        }
        if (!useBillingAsShipping) {
            const shipping = activeShippingAddress;
            if (addressNeedsReview(shipping) && !acceptedAmsFingerprints.has(amsFingerprint(shipping))) {
                return true;
            }
        }
        return false;
    });

    // Shared gate for the auto-open triggers below. ONLY fires in `default`
    // mode — while the user is editing (`editBilling`, `new`, `select`) every
    // keystroke would otherwise re-trigger because the fingerprint depends on
    // the form fields. Save / Continue have their own inline modal triggers
    // (handleSubmit + the editBilling Speichern button) and the "Jetzt prüfen"
    // link is a manual trigger, so we don't lose coverage.
    function maybeAutoOpenValidation() {
        if (status !== 'active') return;
        if (addressMode !== 'default') return;
        if (validationModal.open) return;
        if (!hasPendingAmsReview) return;
        const next = pickValidationTarget();
        if (next) {
            validationModal = { open: true, ...next };
        }
    }

    // Auto-open the validation modal for a stale pending status — but ONLY on
    // step activation (mount / re-entering User) and on tab-visibility regain.
    // Crucially NOT on address/selection changes: switching the delivery
    // address in the selector is an explicit user action and must not auto-pop
    // the modal (continue-click + "Jetzt prüfen" cover the rest). We track
    // `status` as the sole dependency and `untrack` the address-derived reads
    // so a shippingAddress mutation never re-runs this effect.
    $effect(() => {
        const active = status === 'active';
        untrack(() => {
            if (active) maybeAutoOpenValidation();
        });
    });

    // Re-check when the tab becomes visible again (e.g. user came back after
    // editing the address in another tab). Listener is bound only while the
    // step is active and cleaned up on teardown.
    $effect(() => {
        if (status !== 'active') return;
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                untrack(() => maybeAutoOpenValidation());
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    });

    // Applies a Google address suggestion onto the active address target,
    // then re-saves. New hash → backend re-validates → status should flip to
    // 'address_correct'. If a 2nd validation still fails we'll just re-prompt
    // for that new shape, same flow.
    async function applyPredictionAndResave(target, suggestion) {
        if (!suggestion) return;
        let targetAddr;
        if (target === 'billing') {
            targetAddr = user.billingAddress;
        } else {
            // Sync the freshly selected delivery row into the store
            // shippingAddress first (same as handleSubmit) so we adopt the
            // suggestion onto — and re-save — the row the user actually
            // selected, not the stale shippingAddress.
            if (activeDelivery) user.shippingAddress.update(activeDelivery);
            targetAddr = user.shippingAddress;
        }
        targetAddr.update({
            street: suggestion.street || targetAddr.street,
            streetNo: suggestion.streetNo || targetAddr.streetNo,
            zip: suggestion.zip || targetAddr.zip,
            city: suggestion.city || targetAddr.city,
            addInfo: suggestion.addInfo ?? targetAddr.addInfo,
        });
        try {
            saving = true;
            await saveUser();
        } finally {
            saving = false;
        }
    }

    // Load the currently selected delivery row (activeDelivery) into the
    // edit form. Single source of truth for BOTH the "Ausgewählte Adresse
    // bearbeiten" link and the validation modal's "Adresse bearbeiten" button
    // — the latter previously only flipped addressMode without populating
    // newAddress / reconstructing packstation state, opening an empty form.
    function editActiveDelivery() {
        const sd = activeDelivery;
        if (!sd) return;
        if (sd.street === 'Packstation') {
            // Reconstruct packstation state from stored oxaddress fields.
            addressType = 'packstation';
            selectedPackstation = {
                name: `Packstation ${sd.streetNo}`,
                address: sd.packstationStreet || '',
                zip: sd.zip,
                city: sd.city,
                number: sd.streetNo,
            };
            packstationNummer = sd.streetNo;
            packstationPlz = sd.zip;
            packstationOrt = sd.city;
            packstationPostnummer = sd.addInfo || '';
            newAddress = new Address();
        } else {
            addressType = 'address';
            selectedPackstation = null;
            newAddress = new Address(sd);
        }
        addressMode = 'new';
        useNewAddress = false;
    }

    // Modal resolve handler — wired into AddressValidationModal.
    // Returns nothing; the caller decides downstream flow via state changes.
    async function handleValidationResolve(action, suggestion) {
        const target = validationModal.target;
        const sourceAddr = target === 'billing' ? user.billingAddress : activeShippingAddress;
        validationModal.open = false;
        if (action === 'use-original') {
            acceptedAmsFingerprints.add(amsFingerprint(sourceAddr));
        } else if (action === 'use-prediction') {
            await applyPredictionAndResave(target, suggestion);
            // If the new state STILL needs review (e.g. Google produced
            // another inferred result), re-open the modal automatically.
            const next = pickValidationTarget();
            if (next) {
                validationModal = { open: true, ...next };
            }
        } else if (action === 'edit') {
            // Drop the user into the right edit surface so they can fix
            // things manually. For logged-in users that's either
            // 'editBilling' (billing) or 'new' with the existing delivery
            // row selected (shipping). For guests we don't switch modes —
            // the AddressForm is already visible inline. We deliberately
            // DON'T add the current fingerprint to acceptedAmsFingerprints
            // here — that would also make the post-save modal skip even
            // when the user didn't actually change anything, letting an
            // unresolved pending status slip past the continue gate.
            if (isLoggedIn) {
                if (target === 'billing') {
                    previousAddressMode = addressMode === 'default' ? 'default' : addressMode;
                    addressMode = 'editBilling';
                } else if (activeDeliveryId) {
                    editActiveDelivery();
                }
            }
        }
    }

    async function handleSubmit() {
        // Block while the Express-resolved address is in a non-shippable
        // country — the user must pick/edit a deliverable address first.
        if (nonShippableCountry) {
            scrollToFirstError(stepEl);
            return;
        }
        if (!validate()) {
            // Shadow-DOM aware: query from the step root, not document.
            scrollToFirstError(stepEl);
            return;
        }

        devLog('btn:continue', {step: 'user', isLoggedIn, isExpressMode});
        devLog('handleSubmit:start', {
            isLoggedIn,
            isExpressMode,
            useBillingAsShipping,
            activeDeliveryId,
            activeDelivery: activeDelivery ? { id: activeDelivery.id, city: activeDelivery.city, countryCode: activeDelivery.countryCode, phone: activeDelivery.phone } : null,
            shippingAddr: { city: user.shippingAddress.city, countryCode: user.shippingAddress.countryCode, phone: user.shippingAddress.phone },
            billingAddr: { city: user.billingAddress.city, countryCode: user.billingAddress.countryCode, phone: user.billingAddress.phone },
        });

        saving = true;
        try {
            // Guest flow: must compose shippingAddress from local form state.
            // Logged-in flow: shipping address is whatever the active delivery selection
            // points to; the sync $effect already keeps userStore.selectedDeliveryAddressId
            // and user.hideShipping aligned.
            if (!isLoggedIn) {
                if (addressType === 'packstation') {
                    user.addressType = 'packstation';
                    user.hideShipping = false;
                    user.shippingAddress.update({
                        name: user.billingAddress.name,
                        street: 'Packstation',
                        streetNo: packstationNummer,
                        zip: packstationPlz,
                        city: packstationOrt,
                        // DHL Packstations are always in Germany — never inherit
                        // the billing country (e.g. CH), which mislabels the
                        // delivery address.
                        countryId: DE_COUNTRY_ID,
                        country: deName,
                        countryCode: 'DE',
                        addInfo: packstationPostnummer,
                        packstationStreet: selectedPackstation?.address || '',
                    });
                } else {
                    user.addressType = 'address';
                    if (billingSameAsDelivery) {
                        // Delivery = billing: copy delivery form data into billingAddress
                        user.billingAddress.update(guestDeliveryAddress);
                        // hideShipping stays true (from $effect), OXID uses billing as shipping
                    } else {
                        // Separate: delivery into shippingAddress, billingAddress already filled
                        user.shippingAddress.update(guestDeliveryAddress);
                        user.hideShipping = false;
                    }
                }
            } else {
                user.addressType = activeAddressType;
                // Snapshot the active delivery row into user.shippingAddress at save time
                // (we removed the reactive auto-sync to avoid clobbering edit drafts).
                if (!useBillingAsShipping && activeDelivery) {
                    user.shippingAddress.update(activeDelivery);
                }
            }

            const success = await saveUser();
            devLog('handleSubmit:saveResult', {
                success,
                shippingAfter: { city: user.shippingAddress.city, countryCode: user.shippingAddress.countryCode },
                billingAfter: { city: user.billingAddress.city, countryCode: user.billingAddress.countryCode },
            });
            if (success) {
                // AMS gate — if the backend flagged the just-saved address as
                // needing review, open the validation modal and BAIL OUT of
                // onContinue(). The modal's resolve handler updates state /
                // re-saves / switches edit mode as needed; the user has to
                // click "Weiter" again to continue past User.
                const pending = pickValidationTarget();
                if (pending) {
                    devLog('handleSubmit:amsReview', {
                        target: pending.target,
                        status: pending.status,
                    });
                    validationModal = { open: true, ...pending };
                    return;
                }
                maybePatchExpressShipping();
                // VAT gate — validate the USt-IdNr. against VIES and, if the
                // backend reports a mismatch, open the checklist modal and BAIL
                // OUT of onContinue(). The modal's resolve handler either accepts
                // (continue) or moves the user into editBilling. Result is still
                // shown on the Order step regardless.
                await validateVatId();
                if (vatNeedsReview()) {
                    vatValidationModal = { open: true };
                    return;
                }
                subRoute = '';
                addressRejection.message = '';
                onContinue();
            } else {
                // Server-side validation errors are in userStore.errors
                errors = { ...errors, ...userStore.errors };
                scrollToFirstError(stepEl);
            }
        } catch (e) {
            errors = { submit: e.message };
            scrollToFirstError(stepEl);
        } finally {
            saving = false;
        }
    }

    $effect(() => {
        fetchUser();
    });

    // Auto-select for logged-in users (runs once after fetchUser populates state).
    //
    // Server is the source of truth for the active selection:
    //   • user.shippingAddress.id   = `session.deladrid`, with fallback to
    //                                  `oxuser.OXDEFAULTDELID` resolved by the backend.
    //
    // If the server has a specific delivery row → use it.
    // If the server has nothing → default to "billing-as-shipping" (matches OXID
    // core behavior on fresh sessions, and respects an explicit "use billing"
    // intent that cleared deladrid/OXDEFAULTDELID).
    let autoSelectInitialized = $state(false);
    $effect(() => {
        if (!isLoggedIn || autoSelectInitialized) return;
        // Wait until the user data has actually populated (fetchUser is async).
        if (!user.id) return;
        const serverDeliveryId = user.shippingAddress?.id;
        if (serverDeliveryId && deliveryAddresses.some(a => a.id === serverDeliveryId)) {
            activeDeliverySelection = { kind: 'address', id: serverDeliveryId };
            initialActiveDeliveryId = serverDeliveryId;
        } else {
            activeDeliverySelection = { kind: 'billing' };
        }
        autoSelectInitialized = true;
    });

    // Hide Packstation-Lieferadressen wenn aktueller Warenkorb keine DHL-Versandart
    // anbietet (Packstation funktioniert nur über DHL). Aktive Packstation-
    // Adresse bleibt sichtbar (sonst stiller Verlust der vorher gewählten
    // Lieferadresse), wird aber visuell nicht geblockt.
    const visibleDeliveryAddresses = $derived(
        hasDhlShipping
            ? deliveryAddresses
            : deliveryAddresses.filter(a => a.street !== 'Packstation' || a.id === initialActiveDeliveryId)
    );

    const sortedDeliveryAddresses = $derived(
        initialActiveDeliveryId
            ? [...visibleDeliveryAddresses].sort((a, b) => (b.id === initialActiveDeliveryId ? 1 : 0) - (a.id === initialActiveDeliveryId ? 1 : 0))
            : visibleDeliveryAddresses
    );

    // Reset the auto-select latch on logout so the next login re-runs the priority logic.
    $effect(() => {
        if (!isLoggedIn && autoSelectInitialized) autoSelectInitialized = false;
    });

    // Sync activeDeliverySelection → userStore + user.hideShipping (the values saveUser() reads).
    // Billing-as-shipping → no deliveryAddressId in mutation, useShippingAsBilling=true.
    // Specific delivery → pass the id so backend knows what to UPDATE/select.
    // For guests: hideShipping is set explicitly in handleSubmit based on billingSameAsDelivery,
    // not from useBillingAsShipping (which is always true for guests since activeDeliverySelection
    // never changes kind to 'address' in the guest flow).
    $effect(() => {
        userStore.selectedDeliveryAddressId = activeDeliveryId;
        if (user && isLoggedIn) user.hideShipping = useBillingAsShipping;
    });

    // user.shippingAddress is synced explicitly at save time (handleSubmit / Speichern),
    // NOT via a reactive effect. A reactive effect would re-fire mid-edit and clobber the
    // user's draft (e.g. while filling "+ Neue Adresse" the active delivery's data would
    // overwrite the form's newAddress).
</script>

{#if status === 'completed'}
    <!-- Compact completed state -->
    <button
        type="button"
        onclick={onActivate}
        class="w-full bg-white rounded-md border border-grey px-5.5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 text-left"
        data-checkout-step="user" data-checkout-state="completed" data-checkout-action="edit-user"
    >
        <div class="flex items-center gap-3">
            <div class="bg-cyprus text-white rounded-full size-7.5 flex items-center justify-center shrink-0">
                <svg class="size-4" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 10l4 4 8-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
            <span class="font-bold text-base">{t('Kontakt & Lieferadresse')}</span>
        </div>
        <Icon name="down" class="w-4 h-4 text-gray-400 shrink-0"/>
    </button>
{:else if status === 'upcoming'}
    <!-- Locked upcoming state -->
    <div class="bg-white rounded-md border border-grey px-5.5 py-4 flex items-center gap-3" data-checkout-step="user" data-checkout-state="upcoming">
        <div class="bg-gray-100 text-gray-103 rounded-full size-7.5 flex items-center justify-center shrink-0">
            <span class="font-bold text-sm">1</span>
        </div>
        <span class="font-bold text-base text-gray-103">{t('Kontakt & Lieferadresse')}</span>
    </div>
{:else}

<div class="flex flex-col gap-4" data-checkout-step="user" bind:this={stepEl}>

    {#if addressRejection.message}
        <div class="bg-scarlet/10 border-2 border-scarlet rounded-lg p-4 text-sm text-scarlet"
             data-checkout-error data-checkout-section="address-rejected" role="alert">
            <p>{addressRejection.message}</p>
        </div>
    {/if}

    {#if nonShippableCountry}
        <div class="bg-scarlet/10 border-2 border-scarlet rounded-lg p-4 text-sm text-scarlet"
             data-checkout-error data-checkout-section="non-shippable-country" role="alert">
            <p class="font-bold mb-1">
                {nonShippableCountryName
                    ? t('Wir versenden nicht nach {land}.', {land: nonShippableCountryName})
                    : t('Wir versenden nicht in das ausgewählte Lieferland.')}
            </p>
            <p>{t('Bitte wähle eine andere Lieferadresse oder ändere die Lieferadresse in deinem PayPal-Konto.')}</p>
        </div>
    {/if}

    <!-- Kontakt & Lieferadresse -->
    <section id="contact-form" class="bg-white rounded-md border border-grey" data-checkout-section="contact-delivery">
        <!-- Header -->
        <div class="flex items-center gap-3 px-5.5 py-4">
            <div class="flex items-center justify-center size-7.5 text-white bg-cyprus rounded-full shrink-0">
                <span class="font-bold text-sm">1</span>
            </div>
            <span class="font-bold text-base grow">{t("Kontakt & Lieferadresse")}</span>
        </div>

        <!-- Content -->
        <div class="flex flex-col gap-5 px-5.5 pb-5.5" data-user-state={isLoggedIn ? 'logged-in' : 'guest'} data-user-mode={isLoggedIn ? addressMode : undefined}>

            {#if isLoggedIn}
                <!-- ═══ LOGGED IN VIEW ═══ -->

                <!-- Greeting -->
                <div class="flex items-center gap-2">
                    <HandShakeIcon class="w-6 h-6 text-elm shrink-0" />
                    <span class="text-elm italic text-base">{t("Hallo")} {user.billingAddress?.name || user.email}!</span>
                </div>

                {#if addressMode === 'default'}
                    <!-- ── State 1: Default (minimal) ── -->
                    {#if isDeliveryEqualBilling()}
                        <!-- Delivery = Billing (Home) -->
                        <div class="flex flex-col gap-3" data-user-layout="combined">
                            <div class="flex items-center justify-between">
                                <span class="font-bold text-sm">{t("Liefer- und Rechnungsadresse:")}</span>
                                {#if allowAddressChange}
                                    <button
                                        onclick={() => { addressMode = 'select'; }}
                                        class="text-elm font-bold uppercase text-xs tracking-wider cursor-pointer hover:underline"
                                        data-checkout-action="change-address"
                                    >{t("ÄNDERN")}</button>
                                {:else}
                                    <span class="text-xs text-gray-400">{t("Über Amazon Pay übermittelt")}</span>
                                {/if}
                            </div>
                            <AddressCard address={{
                                id: null,
                                name: user.billingAddress.name,
                                street: user.billingAddress.street,
                                streetNo: user.billingAddress.streetNo,
                                zip: user.billingAddress.zip,
                                city: user.billingAddress.city,
                                country: user.billingAddress.country,
                                countryCode: user.billingAddress.countryCode,
                                company: user.billingAddress.company,
                                ustid: user.billingAddress.ustid,
                                addInfo: user.billingAddress.addInfo,
                            }} selected={true} />
                        </div>
                    {:else}
                        <!-- Delivery ≠ Billing → show both (billing first, then delivery) -->
                        <div class="flex flex-col gap-3" data-user-layout="split-billing">
                            <div class="flex items-center justify-between">
                                <span class="font-bold text-sm">{t("Rechnungsadresse:")}</span>
                                {#if allowAddressChange}
                                    <button
                                        onclick={() => { previousAddressMode = 'default'; addressMode = 'editBilling'; }}
                                        class="text-elm font-bold uppercase text-xs tracking-wider cursor-pointer hover:underline"
                                        data-checkout-action="edit-billing"
                                    >{t("ÄNDERN")}</button>
                                {:else}
                                    <span class="text-xs text-gray-400">{t("Über Amazon Pay übermittelt")}</span>
                                {/if}
                            </div>
                            <AddressCard address={{
                                id: null,
                                name: user.billingAddress.name,
                                street: user.billingAddress.street,
                                streetNo: user.billingAddress.streetNo,
                                zip: user.billingAddress.zip,
                                city: user.billingAddress.city,
                                country: user.billingAddress.country,
                                countryCode: user.billingAddress.countryCode,
                                company: user.billingAddress.company,
                                ustid: user.billingAddress.ustid,
                                addInfo: user.billingAddress.addInfo,
                            }} selected={true} showRadio={false} />
                        </div>
                        <div class="flex flex-col gap-3" data-user-layout="split-delivery">
                            <div class="flex items-center justify-between">
                                <span class="font-bold text-sm">{t("Lieferadresse:")}</span>
                                {#if allowAddressChange}
                                    <button
                                        onclick={() => { addressMode = 'select'; }}
                                        class="text-elm font-bold uppercase text-xs tracking-wider cursor-pointer hover:underline"
                                        data-checkout-action="change-address"
                                    >{t("ÄNDERN")}</button>
                                {:else}
                                    <span class="text-xs text-gray-400">{t("Über Amazon Pay übermittelt")}</span>
                                {/if}
                            </div>
                            <AddressCard address={activeDelivery} selected={true} />
                        </div>
                    {/if}

                {:else if addressMode === 'select'}
                    <!-- ── State 2: Address selection ── -->
                    <!-- Top: Gespeicherte Rechnungsadresse -->
                    <div class="flex flex-col gap-2" data-checkout-section="saved-billing">
                        <div class="font-bold text-sm">{t("Gespeicherte Rechnungsadresse:")}</div>
                        <AddressCard address={billingAsAddress} selected={true} />
                        {#if allowAddressChange}
                        <button
                            onclick={() => { previousAddressMode = 'select'; addressMode = 'editBilling'; }}
                            class="text-left text-elm font-bold cursor-pointer text-sm"
                            data-checkout-action="edit-billing"
                        >{t("+ Rechnungsadresse bearbeiten")}</button>
                        {/if}
                    </div>

                    <!-- Below: Gespeicherte Lieferadressen -->
                    <div class="flex flex-col gap-2" data-checkout-section="delivery-list" data-user-delivery={useBillingAsShipping ? 'billing' : (activeDeliveryId ?? 'none')}>
                        <div class="font-bold text-sm">{t("Gespeicherte Lieferadressen:")}</div>
                        <!-- Scrollable list: use-billing + saved addresses (max 3 visible) -->
                        <div class="flex flex-col gap-2 overflow-y-auto max-h-[272px] scroll-shadow rounded-lg px-0.5">
                        <!-- Synthetic "deliver to billing" option -->
                        <button
                            type="button"
                            onclick={() => {
                                activeDeliverySelection = { kind: 'billing' };
                                useNewAddress = false;
                                onAddressChanged();
                                // Push deladrid='' to the backend (use billing as
                                // ship-to) + refresh the basket so shipping cost
                                // updates to the billing country immediately,
                                // not only on the next step's saveUser.
                                selectAddress('');
                            }}
                            class="w-full text-left p-3.5 rounded-lg transition-colors cursor-pointer flex items-center gap-3 {useBillingAsShipping ? 'border border-elm bg-elm/5' : 'border border-gray-102 bg-white hover:border-gray-103'}"
                            data-checkout-action="use-billing" data-select-nameed={useBillingAsShipping}
                        >
                            <div class="shrink-0">
                                {#if useBillingAsShipping}
                                    <div class="size-3.5 rounded-full border-2 border-elm flex items-center justify-center">
                                        <div class="size-2 rounded-full bg-elm"></div>
                                    </div>
                                {:else}
                                    <div class="size-3.5 rounded-full border-2 border-gray-102"></div>
                                {/if}
                            </div>
                            <div class="text-xs font-bold text-black">{t("Identisch zur Rechnungsadresse")}</div>
                        </button>
                        {#each sortedDeliveryAddresses as addr (addr.id)}
                            <AddressCard
                                address={addr}
                                selected={activeDeliveryId === addr.id}
                                onselect={(id) => {
                                    activeDeliverySelection = { kind: 'address', id };
                                    useNewAddress = false;
                                    onAddressChanged();
                                    selectAddress(id);
                                }}
                                ondelete={(id) => {
                                    pendingDeleteId = id;
                                    deleteModalOpen = true;
                                }}
                            />
                        {/each}
                        </div>
                        {#if !useBillingAsShipping && activeDelivery}
                            <button
                                onclick={() => editActiveDelivery()}
                                class="text-left text-elm font-bold cursor-pointer text-sm"
                                data-checkout-action="edit-selected"
                            >{t("+ Ausgewählte Adresse bearbeiten")}</button>
                        {/if}
                        <button
                            onclick={() => {
                                newAddress = new Address();
                                addressMode = 'new';
                                useNewAddress = true;
                            }}
                            class="text-left text-elm font-bold cursor-pointer text-sm"
                            data-checkout-action="add-address"
                        >{t("+ Neue Adresse hinzufügen")}</button>
                    </div>

                {:else if addressMode === 'new'}
                    <!-- ── State 3: New/Edit address form ── -->
                    <div class="flex flex-col gap-3" data-user-form={useNewAddress ? 'new-delivery' : 'edit-delivery'}>
                        <div class="flex items-center justify-between">
                            <span class="font-bold text-sm">{t("Adressen bearbeiten:")}</span>
                            <button
                                onclick={() => { addressMode = 'select'; }}
                                class="text-elm font-bold uppercase text-xs tracking-wider cursor-pointer"
                                data-checkout-action="cancel"
                            >{t("ABBRECHEN")}</button>
                        </div>

                        <!-- Context: show which address is being edited -->
                        {#if activeDelivery && !useNewAddress}
                            <AddressCard address={activeDelivery} selected={true} showRadio={false} />
                        {/if}

                        <div class="font-bold text-sm">{t("Lieferart wählen")}</div>

                        <div class="flex flex-col gap-3">
                            <Radiobox
                                name="addressType"
                                value="address"
                                bind:groupValue={addressType}
                                showIcon={true}
                            >
                                <HouseIcon class="w-4.5 h-4.5" />
                                <span>
                                    <span class="font-bold">{t("An meine Adresse liefern")}</span>
                                </span>
                            </Radiobox>
                            {#if hasDhlShipping}
                            <Radiobox
                                name="addressType"
                                value="packstation"
                                bind:groupValue={addressType}
                                showIcon={true}
                            >
                                <BoxIcon class="w-4.5 h-4.5" />
                                <span>
                                    <span class="font-bold hyphens-none">{t("An eine DHL Packstation")}</span>
                                </span>
                            </Radiobox>
                            {/if}
                        </div>

                        {#if addressType === 'packstation'}
                            {#if selectedPackstation}
                                <div class="bg-green/5 border border-green/30 rounded-md p-4">
                                    <div class="flex justify-between items-start">
                                        <div class="text-sm">
                                            <div class="font-bold text-cyprus">{selectedPackstation.name}</div>
                                            <div>{selectedPackstation.address}</div>
                                            <div>{selectedPackstation.zip} {selectedPackstation.city}</div>
                                        </div>
                                        <button
                                            onclick={() => { packstationOpen = true; }}
                                            class="text-elm font-bold cursor-pointer text-sm shrink-0"
                                        >{t("Ändern")}</button>
                                    </div>
                                </div>
                                <div>
                                    <TextInput
                                        name="postnummer"
                                        bind:value={packstationPostnummer}
                                        label={t("Postnummer *")}
                                        placeholder={t("Deine DHL Postnummer")}
                                        class="font-bold"
                                        inputClass="mt-2"
                                    />
                                    <ValidationError message={errors.postnummer}/>
                                </div>
                            {/if}
                            <Packstation
                                bind:isOpen={packstationOpen}
                                onSelect={handlePackstationSelect}
                                showTrigger={!selectedPackstation}
                            />
                            {#if !selectedPackstation}
                                <ValidationError message={errors.packstationNummer}/>
                            {/if}
                        {:else}
                            <hr class="border-grey" />
                            <AddressForm
                                bind:address={newAddress}
                                {errors}
                            />
                        {/if}

                    </div>

                {:else if addressMode === 'editBilling'}
                    <!-- ── State 4: Edit billing (Rechnungsadresse) ── -->
                    <div class="flex flex-col gap-3" data-user-form="edit-billing">
                        <div class="flex items-center justify-between">
                            <span class="font-bold text-sm">{t("Rechnungsadresse bearbeiten:")}</span>
                            <button
                                onclick={() => { addressMode = previousAddressMode; }}
                                class="text-elm font-bold uppercase text-xs tracking-wider cursor-pointer"
                                data-checkout-action="cancel"
                            >{t("ABBRECHEN")}</button>
                        </div>

                        <!-- Context: current billing address -->
                        <AddressCard address={{
                            id: null,
                            name: user.billingAddress.name,
                            street: user.billingAddress.street,
                            streetNo: user.billingAddress.streetNo,
                            zip: user.billingAddress.zip,
                            city: user.billingAddress.city,
                            country: user.billingAddress.country,
                            countryCode: user.billingAddress.countryCode,
                            company: user.billingAddress.company,
                            ustid: user.billingAddress.ustid,
                            addInfo: user.billingAddress.addInfo,
                        }} selected={true} showRadio={false} />

                        <AddressForm
                            bind:address={user.billingAddress}
                            {errors}
                        />
                    </div>

                {/if}

            {:else}
                <!-- ═══ GUEST VIEW ═══ -->

                <!-- Email + Login wrapped in form so password managers can associate fields -->
                <form autocomplete="on" onsubmit={(e) => { e.preventDefault(); if (showLogin) handleLogin(); }} class="flex flex-col gap-4">
                {#if expressLoginRequired}
                    <div class="bg-amber-50 border border-amber-300 rounded-lg p-4 text-sm text-amber-900" data-user-express-login-required data-checkout-error>
                        {t('Diese E-Mail-Adresse konnte nicht registriert werden. Hast du bereits ein Kundenkonto bei uns?')}
                    </div>
                {/if}
                <div>
                    <EmailInput
                        bind:value={user.email}
                        label={t("E-Mail-Adresse *")}
                        placeholder={t("deine.email@beispiel.de")}
                        required={true}
                        class="font-bold"
                        inputClass="mt-2"
                        name="email"
                        autocomplete="email"
                    />
                    <ValidationError message={errors.email}/>
                </div>

                <!-- Login Toggle (always available for guests, even after a guest user record exists) -->
                <div>
                    <button
                        type="button"
                        onclick={() => (showLogin = !showLogin)}
                        class="text-left text-elm font-bold cursor-pointer text-sm"
                        data-checkout-action="show-login" aria-expanded={showLogin}
                    >{t("Bereits Kunde? Hier anmelden")}</button>
                    {#if showLogin}
                        <div class="bg-gray-100 rounded-lg p-4 pt-3.5 mt-3">
                            <PasswordInput
                                bind:value={user.password}
                                label={t("Passwort")}
                                placeholder={t("Dein Passwort")}
                                class="font-bold"
                                inputClass="mt-2"
                                name="password"
                                autocomplete="current-password"
                            />
                            {#if loginError}
                                <p class="text-scarlet text-sm mt-2" data-login-error>{loginError}</p>
                            {/if}
                            <button
                                type="submit"
                                disabled={loginLoading}
                                data-checkout-action="login-submit"
                                class="block w-full py-3 px-4 bg-cyprus rounded-lg cursor-pointer text-white mt-3 disabled:opacity-50"
                            >{loginLoading ? t('Wird angemeldet...') : t('Anmelden')}</button>
                        </div>
                    {/if}
                </div>
                </form>

                <!-- Newsletter -->
                <Checkbox
                    label={t("Ja, ich möchte per E-Mail informiert werden")}
                    name="newsletter"
                    bind:value={user.newsletter}
                    class="text-cyprus font-light"
                />

            {/if}

            <!-- Guest: Lieferart + Address form -->
            {#if !isLoggedIn}
            <!-- div statt fieldset: fieldset hat UA-Default min-width:min-content +
                 margin/padding, was Flex-Shrink bricht und ungewollte Gaps erzeugt.
                 role="group" + aria-label erhalten die a11y-Semantik. -->
            <div role="group" aria-label={t("Lieferart wählen")} class="text-xsm" data-checkout-section="delivery-type" data-select-nameed={addressType}>
                <div class="font-bold text-sm mb-2">{t("Lieferart wählen")}</div>
                <div class="flex flex-col gap-3">
                    <Radiobox
                        name="addressType"
                        value="address"
                        bind:groupValue={addressType}
                        showIcon={true}
                    >
                        <HouseIcon class="w-4.5 h-4.5" />
                        <span>
                            <span class="font-bold">{t("An meine Adresse liefern")}</span>
                            <br /><span class="font-light text-gray-103">{t("Lieferung an deine Wunschadresse")}</span>
                        </span>
                    </Radiobox>
                    {#if hasDhlShipping}
                    <Radiobox
                        name="addressType"
                        value="packstation"
                        bind:groupValue={addressType}
                        showIcon={true}
                    >
                        <BoxIcon class="w-4.5 h-4.5" />
                        <span>
                            <span class="font-bold hyphens-none">{t("An eine DHL Packstation")}</span>
                            <br /><span class="font-light text-gray-103">{t("Flexible Abholung bei DHL")}</span>
                        </span>
                    </Radiobox>
                    {/if}
                </div>
            </div>

            <!-- Packstation: finder + fields + billing address -->
            {#if addressType === "packstation"}
                <div class="flex flex-col gap-3">
                    <hr class="border-grey" />

                    {#if selectedPackstation}
                        <!-- Selected packstation info card -->
                        <div class="bg-green/5 border border-green/30 rounded-md p-4">
                            <div class="flex justify-between items-start">
                                <div class="text-sm">
                                    <div class="font-bold text-cyprus">{selectedPackstation.name}</div>
                                    <div>{selectedPackstation.address}</div>
                                    <div>{selectedPackstation.zip} {selectedPackstation.city}</div>
                                </div>
                                <button
                                    onclick={() => { packstationOpen = true; }}
                                    class="text-elm font-bold cursor-pointer text-sm shrink-0"
                                >{t("Ändern")}</button>
                            </div>
                        </div>
                        <div>
                            <TextInput
                                name="postnummer"
                                bind:value={packstationPostnummer}
                                label={t("Postnummer *")}
                                placeholder={t("Deine DHL Postnummer")}
                                class="font-bold"
                                inputClass="mt-2"
                            />
                            <ValidationError message={errors.postnummer}/>
                        </div>
                    {/if}
                    <Packstation
                        bind:isOpen={packstationOpen}
                        onSelect={handlePackstationSelect}
                        showTrigger={!selectedPackstation}
                    />
                    {#if !selectedPackstation}
                        <ValidationError message={errors.packstationNummer}/>
                    {/if}

                    <hr class="border-grey" />
                    <AddressForm
                        bind:address={user.billingAddress}
                        {errors}
                        label={t("Rechnungsadresse")}
                    />
                </div>

            <!-- Normal address: show address form -->
            {:else if useNewAddress || deliveryAddresses.length === 0}
                <div class="flex flex-col gap-3">
                    <hr class="border-grey" />
                    <AddressForm
                        bind:address={guestDeliveryAddress}
                        {errors}
                    />

                    <Checkbox
                        label={t("Meine Rechnungsadresse ist mit der Lieferadresse identisch")}
                        name="billingSameAsDelivery"
                        bind:value={billingSameAsDelivery}
                        class="text-cyprus font-light"
                    />

                    {#if !billingSameAsDelivery}
                        <hr class="border-grey" />
                        <AddressForm
                            bind:address={user.billingAddress}
                            errors={billingErrors}
                            label={t("Rechnungsadresse")}
                        />
                    {/if}
                </div>
            {/if}
            {/if}

            <!-- Submit Error -->
            <ValidationError message={errors.submit}/>

            <!-- Action Button -->
            {#if isLoggedIn && (addressMode === 'new' || addressMode === 'editBilling')}
                <button
                    class={buttonStyles["elm-button"]}
                    onclick={async () => {
                        saving = true;
                        try {
                            const wasEditBilling = addressMode === 'editBilling';
                            const wasNew = addressMode === 'new';

                            // For 'new' mode: useNewAddress=true means INSERT (no row id);
                            // useNewAddress=false means UPDATE the currently active delivery row.
                            const editingId = (wasNew && !useNewAddress) ? activeDeliveryId : null;

                            // Client-side validation so editing/adding an address surfaces a
                            // friendly hint (e.g. missing name) instead of the server silently
                            // rejecting. For Packstation only the (billing) name is checked —
                            // street/zip/city come from the selected station.
                            {
                                const editAddr = (wasNew && addressType !== 'packstation') ? newAddress : user.billingAddress;
                                const invalid = addressFieldErrors(editAddr, {skipAddressLines: addressType === 'packstation'});
                                const msg = {
                                    name: t('Bitte gib Vor- und Nachname ein.'),
                                    street: t('Bitte gib eine Straße ein.'),
                                    streetNo: t('Bitte gib eine Hausnummer ein.'),
                                    zip: t('Bitte gib eine Postleitzahl ein.'),
                                    city: t('Bitte gib einen Ort ein.'),
                                    countryId: t('Bitte wähle ein Land.'),
                                };
                                const ae = {};
                                for (const k of Object.keys(invalid)) ae[k] = msg[k];
                                if (Object.keys(ae).length > 0) {
                                    errors = { ...errors, ...ae };
                                    saving = false;
                                    return;
                                }
                            }

                            if (wasNew && addressType === 'packstation') {
                                if (!selectedPackstation || !packstationPostnummer) {
                                    errors = {
                                        ...errors,
                                        packstationNummer: !selectedPackstation ? t('Bitte wähle eine Packstation aus.') : '',
                                        postnummer: !packstationPostnummer ? t('Bitte gib deine DHL Postnummer ein.') : '',
                                    };
                                    saving = false;
                                    return;
                                }
                                user.shippingAddress.update({
                                    name: user.billingAddress.name,
                                    street: 'Packstation',
                                    streetNo: packstationNummer,
                                    zip: packstationPlz,
                                    city: packstationOrt,
                                    // DHL Packstations are always in Germany.
                                    countryId: DE_COUNTRY_ID,
                                    country: deName,
                                    countryCode: 'DE',
                                    addInfo: packstationPostnummer,
                                    packstationStreet: selectedPackstation?.address || '',
                                });
                                user.hideShipping = false;
                            } else if (wasNew) {
                                user.shippingAddress.update(newAddress);
                                user.hideShipping = false;
                            }
                            // For editBilling we do NOT touch shippingAddress; saveUser will
                            // simply re-send the existing user.shippingAddress (which mirrors
                            // the active delivery row from fetchUser).

                            // Override the store-synced delivery id for the save call:
                            //   editBilling → leave selection untouched (saveUser must NOT
                            //     INSERT a new oxaddress out of the current shippingAddress
                            //     mirror; if there's an active delivery, keep its id so the
                            //     backend treats it as a no-op update).
                            //   new + useNewAddress=true → null (INSERT new oxaddress)
                            //   new + useNewAddress=false → editingId (UPDATE existing)
                            // After saveUser completes the sync $effect re-runs and re-aligns
                            // userStore.selectedDeliveryAddressId with activeDeliverySelection.
                            if (!wasEditBilling) {
                                userStore.selectedDeliveryAddressId = editingId;
                            }

                            const success = await saveUser();
                            if (success) {
                                await fetchUser();
                                if (wasNew) {
                                    // Server's session.deladrid now points to the new/updated row;
                                    // fetchUser pulled its id into user.shippingAddress.id. Promote it
                                    // to our active selection so UI matches the server.
                                    const newId = user.shippingAddress?.id;
                                    if (newId) {
                                        activeDeliverySelection = { kind: 'address', id: newId };
                                    }
                                }
                                // AMS gate — show the validation modal before
                                // collapsing the edit surface back to 'select'.
                                // We let the user finish their edit flow as-is
                                // (no mode switch on review-required); the
                                // modal handler will move them into editBilling
                                // / 'new' if they pick "Adresse bearbeiten".
                                const pending = pickValidationTarget();
                                if (pending) {
                                    devLog('save:amsReview', {
                                        target: pending.target,
                                        status: pending.status,
                                    });
                                    validationModal = { open: true, ...pending };
                                } else {
                                    // Sync subRoute BEFORE setting addressMode so $effect.pre sees the
                                    // correct subRoute when it fires and does not overwrite addressMode.
                                    const newMode = wasEditBilling ? previousAddressMode : 'select';
                                    subRoute = modeToSubRoute(newMode, false);
                                    addressMode = newMode;
                                    if (wasNew) useNewAddress = false;
                                }
                            } else {
                                errors = { ...errors, ...userStore.errors };
                            }
                        } catch (e) {
                            errors = { ...errors, submit: e.message };
                        } finally {
                            saving = false;
                        }
                    }}
                    disabled={saving}
                    data-checkout-action="save"
                >
                    {saving ? t('Wird gespeichert...') : t("Speichern")}
                </button>
            {:else}
                <button
                    class={buttonStyles["elm-button"]}
                    onclick={() => {
                        if (hasPendingAmsReview) {
                            const next = pickValidationTarget();
                            if (next) validationModal = { open: true, ...next };
                            return;
                        }
                        handleSubmit();
                    }}
                    disabled={saving}
                    data-checkout-action="continue"
                    data-checkout-ams-pending={hasPendingAmsReview ? 'true' : 'false'}
                >
                    {saving ? t('Wird gespeichert...') : t("Weiter zu Versand & Zahlung")}
                </button>
                {#if hasPendingAmsReview}
                    <p
                        class="mt-2 text-sm text-red-600"
                        data-checkout-ams-hint
                    >
                        {t('Bitte prüfe zuerst deine Adresse — wir haben einen möglichen Korrekturvorschlag.')}
                        <button
                            type="button"
                            class="underline ml-1"
                            onclick={() => {
                                const next = pickValidationTarget();
                                if (next) validationModal = { open: true, ...next };
                            }}
                            data-checkout-action="open-ams-modal"
                        >{t('Jetzt prüfen')}</button>
                    </p>
                {/if}
            {/if}
        </div>
    </section>

</div>

{/if}

<ConfirmModal
    bind:open={deleteModalOpen}
    title={t('Lieferadresse löschen')}
    message={t('Möchtest du diese Lieferadresse wirklich entfernen? Das kann nicht rückgängig gemacht werden.')}
    confirmLabel={t('Löschen')}
    cancelLabel={t('Abbrechen')}
    confirmVariant="danger"
    onConfirm={async () => {
        const id = pendingDeleteId;
        pendingDeleteId = null;
        if (!id) return;
        await deleteAddress(id);
        if (activeDeliveryId === id) {
            activeDeliverySelection = { kind: 'billing' };
            // Deleting the active delivery falls back to billing-as-shipping —
            // push deladrid='' + refresh so the shipping cost recomputes for the
            // billing country now, not only at the next step.
            await selectAddress('');
        }
    }}
    onCancel={() => { pendingDeleteId = null; }}
/>

<AddressValidationModal
    bind:open={validationModal.open}
    original={validationModal.original}
    predictions={validationModal.predictions}
    status={validationModal.status}
    onResolve={handleValidationResolve}
/>

<VatValidationModal
    bind:open={vatValidationModal.open}
    checks={userStore.vatValidationResult?.checks ?? null}
    onResolve={handleVatValidationResolve}
/>
